// Context to forward key events deeply into places where someone is listening to them
import {
  createContext,
  type useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

type KeyEvent = React.KeyboardEvent<HTMLDivElement>

interface KeyEventContextType {
  keyEvent: KeyEvent | null
  setKeyEvent: (event: KeyEvent | null) => void
}

const KeyEventContext = createContext<KeyEventContextType | undefined>(
  undefined,
)

export function KeyEventProvider({ children }: { children: React.ReactNode }) {
  const [keyEvent, setKeyEvent] = useState<KeyEvent | null>(null)

  return (
    <KeyEventContext.Provider value={{ keyEvent, setKeyEvent }}>
      {children}
    </KeyEventContext.Provider>
  )
}

export function useKeyEvent(callback: ReturnType<typeof useCallback>) {
  const context = useContext(KeyEventContext)
  if (!context) {
    throw new Error('useKeyEvent must be used within a KeyEventProvider')
  }

  useEffect(() => {
    if (context.keyEvent) {
      callback(context.keyEvent)
      context.setKeyEvent(null)
    }
  }, [context, callback])
}

export function useSetKeyEvent() {
  const context = useContext(KeyEventContext)
  if (!context) {
    throw new Error('useSetKeyEvent must be used within a KeyEventProvider')
  }
  return context.setKeyEvent
}
